import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Check, 
  ArrowRight, 
  Calendar, 
  MessageSquare, 
  FileText, 
  CreditCard,
  Phone,
  Mail,
  MapPin,
  CheckCircle2
} from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();

  // Four core pillars - simple and clear
  const corePillars = [
    {
      icon: Calendar,
      title: 'Sessions',
      description: 'Schedule, track, and manage all your therapy sessions in one place.'
    },
    {
      icon: MessageSquare,
      title: 'WhatsApp Scheduling',
      description: 'Book and confirm sessions directly through WhatsApp commands.'
    },
    {
      icon: FileText,
      title: 'Structured Notes',
      description: 'Turn session conversations into organized clinical documentation.'
    },
    {
      icon: CreditCard,
      title: 'Payments & Invoices',
      description: 'UPI payment links, automatic invoices, and payment tracking.'
    }
  ];

  const planFeatures = [
    'Session scheduling & management',
    'WhatsApp command integration',
    'Structured note templates',
    'UPI payment coordination',
    'Patient records & history',
    'Invoice generation & delivery'
  ];

  // Relatable pain points
  const painPoints = [
    'Rewriting notes late at night',
    'Chasing UPI screenshots',
    'Switching between 5 different apps',
    'Manually tracking every session'
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Navigation */}
      <nav className="border-b border-stone-100 bg-white/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-18 py-4">
            <Link to="/" className="flex items-center gap-2.5" data-testid="logo">
              <div className="w-9 h-9 rounded-xl bg-[#2F5233] flex items-center justify-center">
                <span className="text-white font-semibold text-sm">CS</span>
              </div>
              <span 
                className="font-semibold text-xl text-stone-900 tracking-tight"
                style={{ fontFamily: 'Fraunces, Georgia, serif' }}
              >
                ClarityStack
              </span>
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm font-medium text-stone-600 hover:text-[#2F5233] transition-colors">
                Features
              </a>
              <a href="#pricing" className="text-sm font-medium text-stone-600 hover:text-[#2F5233] transition-colors">
                Pricing
              </a>
              <Link to="/contact" className="text-sm font-medium text-stone-600 hover:text-[#2F5233] transition-colors">
                Contact
              </Link>
            </div>
            
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/login')}
                className="text-stone-600 hover:text-stone-900 font-medium"
                data-testid="login-btn"
              >
                Login
              </Button>
              <Button 
                className="bg-[#2F5233] hover:bg-[#243f28] text-white rounded-full px-6 font-medium shadow-sm"
                onClick={() => navigate('/register')}
                data-testid="get-started-btn"
              >
                Start Free
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Calm and Clear */}
      <section className="pt-16 sm:pt-20 pb-20 sm:pb-24 px-6 lg:px-8 bg-gradient-to-b from-stone-50/50 to-white">
        <div className="max-w-3xl mx-auto text-center">
          {/* Main Headline */}
          <h1 className="mb-6">
            <span 
              className="block text-3xl sm:text-4xl lg:text-5xl font-semibold text-stone-900 leading-tight tracking-tight"
              style={{ fontFamily: 'Fraunces, Georgia, serif' }}
            >
              Be fully present with your clients.
            </span>
            <span 
              className="block text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#2F5233] leading-tight tracking-tight mt-2"
              style={{ fontFamily: 'Fraunces, Georgia, serif' }}
            >
              Let ClarityStack handle the rest.
            </span>
          </h1>
          
          {/* Sub-headline */}
          <p className="text-base sm:text-lg text-stone-600 mb-10 max-w-2xl mx-auto leading-relaxed">
            Scheduling, structured notes, WhatsApp coordination, and UPI billing — all in one calm system built for Indian therapists.
          </p>
          
          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button 
              size="lg"
              className="bg-[#2F5233] hover:bg-[#243f28] text-white rounded-full px-8 py-6 text-base font-medium shadow-lg shadow-[#2F5233]/20"
              onClick={() => navigate('/register')}
              data-testid="hero-cta-btn"
            >
              Start Free
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button 
              variant="outline"
              size="lg"
              className="rounded-full px-8 py-6 text-base font-medium border-stone-300 text-stone-700 hover:bg-stone-50 hover:border-stone-400"
              onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
            >
              View Pricing
            </Button>
          </div>
          
          {/* Simple trust statement */}
          <p className="text-sm text-stone-500">
            14-day free trial • No credit card required
          </p>
        </div>
      </section>

      {/* Problem Section - Human and Relatable */}
      <section className="py-16 sm:py-20 px-6 lg:px-8 bg-stone-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 
            className="text-2xl sm:text-3xl font-semibold text-stone-900 mb-8"
            style={{ fontFamily: 'Fraunces, Georgia, serif' }}
          >
            Stop doing this every day.
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl mx-auto">
            {painPoints.map((pain, index) => (
              <div 
                key={`pain-${pain.slice(0, 20)}`}
                className="flex items-center gap-3 p-4 bg-white rounded-xl border border-stone-200 text-left"
              >
                <div className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0" />
                <span className="text-stone-700">{pain}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Features - Four Pillars */}
      <section id="features" className="py-16 sm:py-24 px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-2xl sm:text-3xl font-semibold text-stone-900 mb-4"
              style={{ fontFamily: 'Fraunces, Georgia, serif' }}
            >
              Everything you need. Nothing you don't.
            </h2>
            <p className="text-stone-600 max-w-xl mx-auto">
              Four simple tools that work together to reduce your admin work.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {corePillars.map((pillar, index) => (
              <div 
                key={`pillar-${pillar.title}`}
                className="bg-stone-50 p-6 sm:p-8 rounded-2xl border border-stone-100 hover:border-[#2F5233]/30 transition-all duration-300"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-[#2F5233]/10 flex items-center justify-center mb-5">
                  <pillar.icon className="h-6 w-6 text-[#2F5233]" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-semibold text-stone-900 mb-2">{pillar.title}</h3>
                <p className="text-stone-600 leading-relaxed">{pillar.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section - Clear Value */}
      <section id="pricing" className="py-16 sm:py-24 px-6 lg:px-8 bg-stone-50">
        <div className="max-w-lg mx-auto">
          <div className="text-center mb-10">
            <h2 
              className="text-2xl sm:text-3xl font-semibold text-stone-900 mb-4"
              style={{ fontFamily: 'Fraunces, Georgia, serif' }}
            >
              Simple, honest pricing.
            </h2>
          </div>
          
          {/* Single Pricing Card */}
          <div className="bg-white rounded-2xl border-2 border-[#2F5233] p-6 sm:p-8 shadow-lg shadow-[#2F5233]/10" data-testid="pricing-card">
            <div className="text-center mb-6">
              <div className="flex items-baseline justify-center gap-1 mb-2">
                <span 
                  className="text-4xl sm:text-5xl font-semibold text-stone-900"
                  style={{ fontFamily: 'Fraunces, Georgia, serif' }}
                >
                  ₹1,499
                </span>
                <span className="text-stone-500 text-lg">/month</span>
              </div>
              <p className="text-xs text-stone-400 mb-4">Inclusive of GST</p>
              
              {/* Value Framing */}
              <div className="bg-[#2F5233]/5 rounded-xl p-4 mb-6">
                <p className="text-sm text-stone-700 leading-relaxed">
                  If ClarityStack helps you take just <span className="font-semibold">one additional session per week</span>, it pays for itself.
                </p>
              </div>
            </div>
            
            <ul className="space-y-3 mb-8">
              {planFeatures.map((feature) => (
                <li key={`feat-${feature.slice(0, 20)}`} className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-[#2F5233] flex-shrink-0 mt-0.5" />
                  <span className="text-stone-600">{feature}</span>
                </li>
              ))}
            </ul>
            
            <Button 
              size="lg"
              className="w-full bg-[#2F5233] hover:bg-[#243f28] text-white rounded-full font-medium py-6"
              onClick={() => navigate('/register')}
              data-testid="pricing-cta-btn"
            >
              Start Free Trial
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            
            <p className="text-center text-sm text-stone-500 mt-4">
              14-day free trial • Cancel anytime
            </p>
          </div>
          
          {/* Additional value statement */}
          <p className="text-center text-sm text-stone-500 mt-6">
            Saves you hours of admin every week — that's time for extra clients or real rest.
          </p>
        </div>
      </section>

      {/* Advanced Clinical Insights - Early Access */}
      <section className="py-12 sm:py-16 px-6 lg:px-8 bg-white">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-block px-3 py-1 text-xs font-medium text-[#2F5233] bg-[#2F5233]/10 rounded-full mb-4">
              Early Access
            </span>
            <h2 
              className="text-xl sm:text-2xl font-semibold text-stone-900 mb-3"
              style={{ fontFamily: 'Fraunces, Georgia, serif' }}
            >
              Advanced Clinical Insights
            </h2>
            <p className="text-stone-600 text-sm sm:text-base leading-relaxed max-w-xl mx-auto">
              We're building deeper tools to help you understand your clients' progress over time — 
              with language-aware insights designed for the Indian context.
            </p>
          </div>
          
          {/* Future features - simple list */}
          <div className="bg-stone-50 rounded-2xl p-6 sm:p-8 mb-6">
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-stone-200/60 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="h-4 w-4 text-stone-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                  </svg>
                </div>
                <div>
                  <span className="font-medium text-stone-800">Track progress across sessions</span>
                  <p className="text-sm text-stone-500 mt-0.5">See how themes and patterns evolve over weeks and months</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-stone-200/60 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="h-4 w-4 text-stone-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                </div>
                <div>
                  <span className="font-medium text-stone-800">Voice and tone awareness</span>
                  <p className="text-sm text-stone-500 mt-0.5">Subtle cues from how clients express themselves</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-stone-200/60 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="h-4 w-4 text-stone-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                  </svg>
                </div>
                <div>
                  <span className="font-medium text-stone-800">Indian language support</span>
                  <p className="text-sm text-stone-500 mt-0.5">Hindi, Marathi, Tamil, and more — as clients actually speak</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-stone-200/60 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="h-4 w-4 text-stone-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div>
                  <span className="font-medium text-stone-800">Cultural context awareness</span>
                  <p className="text-sm text-stone-500 mt-0.5">Family dynamics, social pressures, and local realities</p>
                </div>
              </li>
            </ul>
          </div>
          
          <div className="text-center">
            <Button 
              variant="outline"
              size="lg"
              className="rounded-full px-8 border-stone-300 text-stone-700 hover:bg-stone-50"
              onClick={() => navigate('/contact')}
              data-testid="waitlist-btn"
            >
              Join Waitlist
            </Button>
            <p className="text-xs text-stone-400 mt-3">
              Be first to know when these features are ready
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 px-6 lg:px-8 bg-[#2F5233]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 
            className="text-2xl sm:text-3xl font-semibold text-white mb-4"
            style={{ fontFamily: 'Fraunces, Georgia, serif' }}
          >
            Ready to reclaim your time?
          </h2>
          <p className="text-white/70 mb-8">
            Join therapists across India who've simplified their practice.
          </p>
          <Button 
            size="lg"
            className="bg-white text-[#2F5233] hover:bg-stone-100 rounded-full px-8 font-medium"
            onClick={() => navigate('/register')}
            data-testid="footer-cta-btn"
          >
            Start Free
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 sm:py-16 px-6 lg:px-8 bg-stone-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-10">
            {/* Company Info */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-9 h-9 rounded-xl bg-[#2F5233] flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">CS</span>
                </div>
                <span 
                  className="font-semibold text-xl tracking-tight"
                  style={{ fontFamily: 'Fraunces, Georgia, serif' }}
                >
                  ClarityStack
                </span>
              </div>
              <p className="text-stone-400 text-sm mb-6">
                A calm WhatsApp-native assistant for Indian therapists.
              </p>
              <div className="text-sm text-stone-400 space-y-2">
                <p className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 text-stone-500" />
                  <span>B104 Shantideep Society, Sathya Sai Complex,<br />Chikuwadi, Borivali West, Mumbai – 400092</span>
                </p>
                <p className="flex items-center gap-3">
                  <Phone className="h-4 w-4 flex-shrink-0 text-stone-500" />
                  <span>+91 9967124157</span>
                </p>
                <p className="flex items-center gap-3">
                  <Mail className="h-4 w-4 flex-shrink-0 text-stone-500" />
                  <span>support@claritystack.live</span>
                </p>
              </div>
            </div>
            
            {/* Links */}
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-3 text-sm">
                <li><a href="#features" className="text-stone-400 hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="text-stone-400 hover:text-white transition-colors">Pricing</a></li>
                <li><Link to="/contact" className="text-stone-400 hover:text-white transition-colors">Contact</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-3 text-sm">
                <li><Link to="/privacy" className="text-stone-400 hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-stone-400 hover:text-white transition-colors">Terms & Conditions</Link></li>
                <li><Link to="/refund" className="text-stone-400 hover:text-white transition-colors">Refund Policy</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-stone-800">
            <p className="text-sm text-stone-500 text-center">
              © {new Date().getFullYear()} Think Through Enterprise. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
